import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const STUDIO_1 = "/assets/wolf-portrait.png";
const STUDIO_2 = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/fjfvvqkk_CE1C5F7F-965F-4E40-A7DD-475C6FCA0E2F.PNG";
const OCEAN = "https://images.unsplash.com/photo-1769269892590-18bed18fc96c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxkYXJrJTIwb2NlYW4lMjB3YXZlcyUyMGNsaWZmcyUyMG1vb2R5fGVufDB8fHx8MTc3OTMyMTA1N3ww&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.2, 0.7, 0.2, 1] } },
};

export default function About() {
  return (
    <div className="pt-32 sm:pt-40 pb-20 bg-[#080808] min-h-screen" data-testid="about-page">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-6">— About</p>
        <h1 className="font-display wolf-gold-foil text-6xl sm:text-8xl lg:text-9xl leading-[0.85]" data-testid="about-title">
          Mark Leslie
        </h1>
        <p className="font-editorial italic text-2xl sm:text-3xl text-white/70 mt-6 max-w-3xl">
          WOLF is the sound of survival turning into purpose.
        </p>
      </div>

      {/* SECTION 1 */}
      <section className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade}
          className="lg:col-span-7 aspect-[4/5] overflow-hidden"
        >
          <img src={STUDIO_1} alt="Studio" className="w-full h-full object-cover hero-drift" />
        </motion.div>
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade}
          className="lg:col-span-5 lg:pt-20 self-start"
        >
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— The Project</p>
          <h2 className="font-display text-5xl sm:text-6xl text-white leading-[0.9]">
            Chaos<br/>and clarity.
          </h2>
          <p className="font-editorial italic text-xl text-white/70 mt-8 leading-relaxed">
            Created by Australian artist and songwriter Mark Leslie, WOLF blends raw grunge energy,
            heavy riffs, spoken truth, and emotional vulnerability into music that hits with both
            chaos and clarity.
          </p>
          <p className="font-body text-white/55 mt-6 leading-relaxed">
            Influenced by bands like Royal Blood, Audioslave, Chris Cornell, and The Butterfly Effect,
            the project was born from real-life pain, resilience, faith, and rebirth.
          </p>
        </motion.div>
      </section>

      {/* PULL QUOTE */}
      <section className="my-32 px-6 sm:px-10 lg:px-16 max-w-[1300px] mx-auto text-center">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1 }}
          className="font-editorial italic text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.1]"
        >
          "This isn't polished perfection.<br className="hidden sm:inline" /> It's scars, soul, fire, and truth."
        </motion.p>
      </section>

      {/* OCEAN */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden" data-testid="about-ocean-section">
        <div className="absolute inset-0 hero-drift" style={{ backgroundImage: `url(${OCEAN})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center">
          <div className="max-w-xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— The Story</p>
            <h3 className="font-display text-5xl sm:text-7xl text-white leading-[0.9]">
              From the<br/>ground up.
            </h3>
            <p className="font-editorial italic text-white/70 text-lg sm:text-xl mt-6 max-w-md">
              After surviving a life-changing spinal injury, years of personal battles, and rebuilding
              from the ground up, WOLF became more than music — it became identity.
            </p>
            <p className="font-body text-white/65 text-base mt-4 max-w-md leading-relaxed">
              Every track carries themes of redemption, brotherhood, grief, strength, spirituality,
              and awakening.
            </p>
          </div>
        </div>
      </section>

      {/* ORIGIN TIMELINE */}
      <section className="mt-32 px-6 sm:px-10 lg:px-16 max-w-[1500px] mx-auto" data-testid="origin-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— Origin</p>
            <h2 className="font-display text-5xl sm:text-6xl text-white leading-[0.9]">
              The fire<br/>was always<br/>there.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-8">
            <p className="font-editorial italic text-xl text-white/70 leading-relaxed">
              From the first toy guitar in the lounge room, to campsite acoustic nights, to the
              first stage. The instrument changed. The fire never did.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {[
            { src: "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/ymaxl1wt_IMG_5074.jpeg", caption: "The first guitar." },
            { src: "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/9ntp1wh1_IMG_5072.jpeg", caption: "The first songs." },
            { src: "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/fhum4jl6_IMG_5073.jpeg", caption: "The first stage." },
          ].map((img, i) => (
            <motion.figure
              key={img.src}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.2, 0.7, 0.2, 1] }}
              className="group relative bg-black border border-white/10 overflow-hidden"
              data-testid={`origin-photo-${i}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={img.src}
                  alt={img.caption}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1400ms] group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-5 left-5 right-5">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/80">— Chapter {i + 1}</p>
                <p className="font-editorial italic text-2xl text-white mt-1">{img.caption}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* SECTION 2 */}
      <section className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade}
          className="lg:col-span-5 self-start lg:pt-20 lg:col-start-1 order-2 lg:order-1"
        >
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— The Pack</p>
          <h2 className="font-display text-5xl sm:text-6xl text-white leading-[0.9]">
            For the<br/>outsiders.
          </h2>
          <p className="font-editorial italic text-xl text-white/70 mt-8 leading-relaxed">
            WOLF is for the outsiders, the fighters, the survivors, and anyone learning how to become
            themselves again.
          </p>
          <p className="font-body text-white/55 mt-6 leading-relaxed text-2xl">
            🐺 ✝
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/music" data-testid="about-music-cta" className="text-[11px] uppercase tracking-[0.32em] text-white border border-white/20 hover:border-[#D4AF37] px-5 py-3 inline-flex items-center gap-2">
              The Music <ArrowRight size={14}/>
            </Link>
            <Link to="/den" data-testid="about-den-cta" className="text-[11px] uppercase tracking-[0.32em] text-white border border-white/20 hover:border-[#D4AF37] px-5 py-3 inline-flex items-center gap-2">
              The Den <ArrowRight size={14}/>
            </Link>
          </div>
        </motion.div>
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade}
          className="lg:col-span-7 aspect-square overflow-hidden order-1 lg:order-2 bg-black"
        >
          <img src={STUDIO_2} alt="WOLF — Faith. Music. Purpose." className="w-full h-full object-contain" />
        </motion.div>
      </section>

      {/* THEMES */}
      <section className="mt-32 max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-6">— Themes</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-y-6 gap-x-8 font-display text-3xl sm:text-4xl text-white/90 tracking-tight">
          {["Resilience","Survival","Rebuilding","Music","Surfing","Spirituality","Transformation"].map((t, i) => (
            <motion.span
              key={t}
              data-testid={`about-theme-${i}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="border-b border-white/10 pb-3"
            >
              {t}
            </motion.span>
          ))}
        </div>
      </section>
    </div>
  );
}
